import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import _ from 'lodash';
import { sumBy } from 'lodash/math';
import moment from 'moment';

import { DATE_FORMAT } from 'components/pages/ModeReports/modeReportsServices';

import PatientStipendApi from '../../../../../../../../api/patient/PatientStipendApi';
import ModalBoxes from '../../../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import NotificationManager from '../../../../../../../../common/notifications/NotificationManager';
import { PATIENT_STIPEND } from '../../../../../../../../constants/notificationMessages';
import { STTS_ESOURCE_TYPES } from '../../../../../../../../constants/study/studyTypes';
import { toFormattedBill } from '../../../../../../../../services/financial';
import { muiDataGridLocaleText } from '../../../../../../../../services/i18n/mui';
import { NSPP_STIPEND } from '../../../../../../../../services/i18n/namespaces';
import { PatientInfoContext } from '../../../PatientInfoContext';
import PaymentsSectionHeader from '../PaymentsSectionHeader/PaymentsSectionHeader';

import { AddUnexpectedVisitButton } from './AddUnexpectedVisitButton';
import PaidStipendItemModal from './PaidStipendItemModal';
import useStipendGridData from './useStipendGridData';
const { isMoment } = moment;

const initialState = {
  columns: {
    columnVisibilityModel: {}
  }
};

export default function StipendSection() {
  const { t } = useTranslation();
  const { ssuPatientId } = useParams();
  const { currentStudy, patientInfo } = useContext(PatientInfoContext);
  const studyTypes = currentStudy.types;
  const studyType = currentStudy.type;
  const [columns, data, setData] = useStipendGridData(studyTypes);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(() => {
    if (!ssuPatientId) return;
    setLoading(true);

    PatientStipendApi.getBySitePatientId(ssuPatientId)
      .then(response => {
        setData(
          response.data.map((item, index) => ({
            ...item,
            id: item.sitePatientEncounterId || item.protocolEncounterKey || index
          }))
        );
      })
      .catch(() => {
        NotificationManager.error(PATIENT_STIPEND);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [ssuPatientId, setData]);

  function onStipendPay(encounter, encounterDate, selectedItems) {
    const request = {
      handlerName: encounter.handlerName,
      encounterDate: isMoment(encounterDate) && moment(encounterDate).format(DATE_FORMAT),
      sitePatientEncounterId: encounter.sitePatientEncounterId,
      situationalEncounterId: encounter.situationalEncounterId,
      protocolEncounterId: encounter.protocolEncounterId,
      protocolEncounterKey: encounter.protocolEncounterKey,
      selectedItems
    };

    const stipendOversight = _.some(selectedItems, 'stipendOversight');
    const amount = sumBy(selectedItems, 'amount');

    PatientStipendApi.triggerStipendPayment(ssuPatientId, request).then(
      function() {
        loadData();
        if (stipendOversight) {
          NotificationManager.success('The stipend request was submitted for review');
        } else {
          NotificationManager.success(
            `${toFormattedBill(amount)} amount of patient stipend was successfully queued for processing`
          );
        }
      },
      function() {
        if (stipendOversight) {
          NotificationManager.error(
            'The stipend request failed to be submitted for review, please try again or contact support'
          );
        } else {
          NotificationManager.error(
            `${toFormattedBill(
              amount
            )} amount of patient stipend failed to be queued for processing, please try again or contact support`
          );
        }
      }
    );
  }

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="payments-stipend-section">
      <PaymentsSectionHeader title={t(`${NSPP_STIPEND}:patientStipendsHeader`)}>
        <AddUnexpectedVisitButton onStipendPay={onStipendPay} />
      </PaymentsSectionHeader>
      <DataGridPremium
        rowHeight={38}
        rows={data}
        columns={columns}
        disableRowSelectionOnClick
        loading={loading}
        initialState={initialState}
        localeText={muiDataGridLocaleText({ noRowsLabel: t('noResultsToDisplay') })}
        onRowClick={function({ row }) {
          ModalBoxes.open({
            component: (
              <PaidStipendItemModal
                viewOnlyMod={isESourceType(studyTypes)}
                encounter={row}
                itemsData={row?.items}
                onStipendPay={onStipendPay}
                patientInfo={patientInfo}
                studyType={studyType}
              />
            ),
            onClose: function(isEncounterDateUpdated) {
              if (isEncounterDateUpdated) {
                loadData();
              }
            }
          });
        }}
        disableRowGrouping
        disableAggregation
        disablePivoting
      />
    </div>
  );
}

function isESourceType(types) {
  return types.some(function(type) {
    return STTS_ESOURCE_TYPES.includes(type);
  });
}
